import { type ClassValue, clsx } from 'clsx';

// Production-safe logger — suppresses console output in production builds
// to avoid Lighthouse "Browser errors logged to console" penalty.
const __DEV__ = import.meta.env.DEV;

export const logger = {
  log: (...args: unknown[]) => { if (__DEV__) console.log(...args); },
  warn: (...args: unknown[]) => { if (__DEV__) console.warn(...args); },
  error: (...args: unknown[]) => { if (__DEV__) console.error(...args); },
};

// Simple class name merger (without tailwind-merge for now)
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}

// Format relative time
export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const target = typeof date === 'string' ? new Date(date) : date;
  const diffMs = target.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000);

  if (diffMins < 0) {
    const absMins = Math.abs(diffMins);
    if (absMins < 60) return `${absMins}m ago`;
    const hours = Math.floor(absMins / 60);
    if (hours < 24) return `${hours}h ago`;
    return target.toLocaleDateString();
  }

  if (diffMins < 60) return `in ${diffMins}m`;
  const hours = Math.floor(diffMins / 60);
  if (hours < 24) return `in ${hours}h`;
  return target.toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Local calendar-day helpers
//
// "Tomorrow" means the next calendar day in the user's timezone, not "24-48h
// from now". Dividing elapsed hours by 24 labels a Tuesday-afternoon event as
// "Tomorrow" on a Sunday night (39h away), which is the bug these fix.
// Date keys are YYYY-MM-DD built from local getters — never toISOString(),
// whose UTC date is a day off for anyone east of UTC or in BST after 23:00.
// ---------------------------------------------------------------------------

/** Local midnight of the given date. */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Whole local calendar days from `now` to `target` (0 = later today, 1 = tomorrow, negative = past). */
export function calendarDaysUntil(target: Date | string, now: Date = new Date()): number {
  const t = typeof target === 'string' ? new Date(target) : target;
  const ms = startOfLocalDay(t).getTime() - startOfLocalDay(now).getTime();
  // Round, not floor: a DST shift makes one "day" 23h or 25h.
  return Math.round(ms / 86_400_000);
}

/** YYYY-MM-DD in the user's local timezone. */
export function toLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Inverse of toLocalDateKey: local midnight of that day. */
export function parseLocalDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Format time for events (e.g., "Today at 3:00 PM")
export function formatEventTime(date: Date | string): string {
  const target = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const isToday = target.toDateString() === now.toDateString();

  const time = target.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isToday) {
    return `Today at ${time}`;
  }

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (target.toDateString() === tomorrow.toDateString()) {
    return `Tomorrow at ${time}`;
  }

  return `${target.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${time}`;
}

// Resolve the public-facing display name for a person. We collect a real name
// (first_name/last_name — kept for admin/moderation), a display name
// (profile_name) and a username (the /u/<handle> link only, never shown as a
// name). User-facing screens, chat and push notifications all show the display
// name; this falls back to first_name, then a generic label, so it's safe to
// call on partially-loaded profiles. The database twin is display_name_of()
// (migration 112): keep the two in step.
export function getDisplayName(
  person: { profile_name?: string | null; first_name?: string | null } | null | undefined,
  fallback = 'User',
): string {
  const name = person?.profile_name?.trim() || person?.first_name?.trim();
  return name || fallback;
}

// Resolve the name + avatar to show for a chat/DM sender. Business accounts
// speak as their venue (logo + business name), everyone else uses their
// personal profile name + avatar.
export function getChatIdentity(
  sender:
    | {
        profile_name?: string | null;
        first_name?: string | null;
        avatar_url?: string | null;
        account_type?: string | null;
        business?: { name: string; logo_url: string | null } | null;
      }
    | null
    | undefined,
  fallback = 'User',
): { name: string; avatarUrl: string | null } {
  if (sender?.account_type === 'business' && sender.business?.name) {
    return { name: sender.business.name, avatarUrl: sender.business.logo_url ?? null };
  }
  return { name: getDisplayName(sender, fallback), avatarUrl: sender?.avatar_url ?? null };
}

// Calculate age from date of birth
export function calculateAge(dob: Date | string): number {
  const birthDate = typeof dob === 'string' ? new Date(dob) : dob;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

// Calculate distance between two coordinates (Haversine formula)
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Format distance for display
export function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)}m`;
  }
  return `${km.toFixed(1)}km`;
}

// Truncate text with ellipsis
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

// Debounce function
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
